import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideLayoutTemplate,
  lucidePlus,
  lucideTriangleAlert,
} from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { CompanyService } from '../../../../../services/company.service';
import {
  VoucherDesign,
  VoucherDesignService,
  VoucherType,
  voucherTypeLabel,
} from '../../../../../services/voucher-design.service';
import { DesignCardComponent } from './design-card/design-card.component';

/** One voucher type's designs: the printed one first, then the rest. */
export interface DesignGroup {
  type: VoucherType;
  label: string;
  /** The design printed for this type; null means the standard template. */
  active: VoucherDesign | null;
  /** Active first, then the others in their list order. */
  designs: VoucherDesign[];
  /** The product group only matters while product vouchers are on. */
  enabled: boolean;
}

const TYPES: VoucherType[] = ['money', 'product'];

/**
 * All designs, grouped by voucher type. Each group shows which design is
 * printed and lets the user pick another one right there ("Verwenden"), or
 * start a new design of that type.
 */
@Component({
  selector: 'app-design-library',
  standalone: true,
  imports: [
    RouterModule,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmSkeletonImports,
    HlmTypographyImports,
    DesignCardComponent,
  ],
  providers: [provideIcons({ lucideCheck, lucideLayoutTemplate, lucidePlus, lucideTriangleAlert })],
  templateUrl: './design-library.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Block, so the parent's space-y gap between the cards applies to this host too.
  host: { class: 'block' },
})
export class DesignLibraryComponent {
  private readonly designService = inject(VoucherDesignService);
  private readonly companyService = inject(CompanyService);

  readonly designs = input<VoucherDesign[]>([]);
  readonly loading = input(false);
  readonly busyId = input<string | null>(null);
  readonly previewingId = input<string | null>(null);

  readonly activate = output<VoucherDesign>();
  readonly copy = output<{ design: VoucherDesign; voucherType: VoucherType }>();
  readonly delete = output<VoucherDesign>();
  readonly preview = output<VoucherDesign>();
  /** Start a new design of this voucher type. */
  readonly create = output<VoucherType>();

  readonly groups = computed<DesignGroup[]>(() => {
    const company = this.companyService.company();
    const designs = this.designs();
    return TYPES.map((type) => {
      const active = this.designService.activeDesign(designs, type);
      const others = designs.filter((d) => d.voucher_type === type && d !== active);
      return {
        type,
        label: voucherTypeLabel(type),
        active,
        designs: active ? [active, ...others] : others,
        enabled: type === 'money' || !!company?.voucher_products_enabled,
      };
    });
  });
}
